import React from 'react';
import { useTheme } from '../../theme';
import { SpaceScale } from '../../theme/tokens';
import { CSSDimension, ResponsiveValue, Theme } from '../../theme/types';
import Box, { HTMLProps } from '../Box';

interface AppearanceProps {
  size?: ResponsiveValue<SpaceScale>;
  flexBasis?: ResponsiveValue<SpaceScale>;
  direction?: 'vertical' | 'horizontal';
}

export type SpaceProps = HTMLProps & AppearanceProps;

const calculateFlexBasis = (size: SpaceScale, theme: Theme): CSSDimension => {
  const calculatedSize = theme.tokens.spacingScale * size;
  return `${calculatedSize}px` as CSSDimension;
};

const transformSizeToFlexBasis = (
  size: ResponsiveValue<SpaceScale>,
  theme: Theme
): ResponsiveValue<CSSDimension> => {
  if (!size) return;

  return (Array.isArray(size)
    ? size.map((s) => (s ? calculateFlexBasis(s, theme) : s))
    : calculateFlexBasis(size, theme)) as ResponsiveValue<CSSDimension>;
};

const Space: React.FC<SpaceProps> = React.memo(function Space(props) {
  const {
    direction = 'vertical',
    size = 3,
    flexBasis = undefined,
    ...rest
  } = props;
  const { theme } = useTheme();
  const paddingBottom =
    direction === 'vertical' && !flexBasis ? size : undefined;
  const paddingRight =
    direction === 'horizontal' && !flexBasis ? size : undefined;

  return (
    <Box
      display={direction === 'vertical' ? 'block' : 'inline-block'}
      paddingBottom={paddingBottom}
      paddingRight={paddingRight}
      flexGrow={0}
      flexShrink={0}
      flexBasis={transformSizeToFlexBasis(flexBasis, theme)}
      {...rest}
    />
  );
});

export default Space;
